import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@/platform/auth";
import { profileMutations, profileQueries } from "@/features/profile";
import { useTenant } from "@/domains/tenant/hooks";
import {
  formatCreditPeriodRemaining,
  formatDisplayCredits,
} from "@/domains/billing/format";
import { billingQueries } from "@/domains/billing/queries";
import {
  deleteTenantVariable,
  tenantKeys,
  tenantQueries,
  toTenantVariableViews,
  upsertTenantVariables,
  upsertTenantVariableValue,
} from "@/domains/tenant";
import type {
  TenantVariableDraft,
  TenantVariableValueDraft,
} from "@/domains/tenant";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/shared/components/ui/alert";
import { ContactSalesButton } from "@/domains/billing/components";
import { TenantVariableList } from "@/domains/tenant/components";
import { Loader2, CircleAlert } from "lucide-react";

type ProfileFormValues = {
  firstname: string;
  lastname: string;
  position: string;
};

export function ProfilePage() {
  const { user } = useSession();
  const queryClient = useQueryClient();

  const {
    data: profileData,
    isLoading: isProfileLoading,
    error: queryError,
  } = useQuery(profileQueries.current(user?.id));

  const {
    data: tenantData,
    isLoading: isTenantLoading,
    error: tenantError,
  } = useTenant();
  const tenantId = tenantData?.tenant.tenant_id ?? "";
  const { data: creditUsage } = useQuery(billingQueries.currentCreditUsage());
  const {
    data: tenantVariableConfig,
    isLoading: isTenantVariablesLoading,
    error: tenantVariablesError,
  } = useQuery(tenantQueries.variables(tenantId));

  const tenantVariables = useMemo(
    () =>
      tenantId
        ? toTenantVariableViews({
            variables: tenantVariableConfig?.variables ?? [],
            values: tenantVariableConfig?.variable_values ?? [],
            currentTenantId: tenantId,
          })
        : [],
    [tenantId, tenantVariableConfig],
  );

  const invalidateTenantVariables = () => {
    void queryClient.invalidateQueries({
      queryKey: tenantKeys.variables().queryKey,
    });
  };

  const {
    mutate: updateProfile,
    isPending,
    error: updateError,
  } = useMutation({
    mutationFn: async (values: ProfileFormValues) => {
      if (!profileData?.tenant_user_id)
        throw new Error("Tenant user not found");
      return profileMutations.updateProfile(profileData?.tenant_user_id, {
        firstname: values.firstname || null,
        lastname: values.lastname || null,
        position: values.position || null,
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: profileQueries.all.queryKey,
      });
    },
  });

  const {
    mutate: createVariable,
    isPending: isCreatingVariable,
    error: createVariableError,
  } = useMutation({
    mutationFn: (draft: TenantVariableDraft) => {
      if (!tenantId) throw new Error("Tenant not found");
      return upsertTenantVariables({ tenantId, variables: [draft] });
    },
    onSuccess: invalidateTenantVariables,
  });

  const {
    mutate: updateVariable,
    isPending: isUpdatingVariable,
    error: updateVariableError,
  } = useMutation({
    mutationFn: (params: {
      variableId: string;
      draft: TenantVariableDraft;
    }) => {
      if (!tenantId) throw new Error("Tenant not found");
      return upsertTenantVariables({
        tenantId,
        variables: [{ ...params.draft, variable_id: params.variableId }],
      });
    },
    onSuccess: invalidateTenantVariables,
  });

  const {
    mutate: removeVariable,
    isPending: isDeletingVariable,
    error: deleteVariableError,
  } = useMutation({
    mutationFn: (variableId: string) => {
      if (!tenantId) throw new Error("Tenant not found");
      return deleteTenantVariable({ tenantId, variableId });
    },
    onSuccess: invalidateTenantVariables,
  });

  const {
    mutate: saveVariableValue,
    isPending: isSavingVariableValue,
    error: saveVariableValueError,
  } = useMutation({
    mutationFn: (value: TenantVariableValueDraft) => {
      if (!tenantId) throw new Error("Tenant not found");
      return upsertTenantVariableValue({ tenantId, value });
    },
    onSuccess: invalidateTenantVariables,
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
  } = useForm<ProfileFormValues>({
    defaultValues: {
      firstname: "",
      lastname: "",
      position: "",
    },
    values: profileData
      ? {
          firstname: profileData.firstname || "",
          lastname: profileData.lastname || "",
          position: profileData.position || "",
        }
      : undefined,
  });

  const onSubmit = (values: ProfileFormValues) => {
    updateProfile(values, {
      onSuccess: () => {
        reset(values);
      },
    });
  };

  const isLoading = isProfileLoading || isTenantLoading;
  const error = queryError || tenantError;
  const tenantVariableError =
    tenantVariablesError ||
    createVariableError ||
    updateVariableError ||
    deleteVariableError ||
    saveVariableValueError;

  if (isLoading) {
    return (
      <>
        <title>Profile - Researchly</title>
        <div className="flex min-h-[70vh] items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <title>Profile - Researchly</title>
        <div className="flex min-h-[70vh] items-center justify-center p-4">
          <Alert variant="destructive" className="max-w-md">
            <CircleAlert />
            <AlertTitle>Error loading profile</AlertTitle>
            <AlertDescription>
              {error instanceof Error
                ? error.message
                : "Failed to load profile data"}
            </AlertDescription>
          </Alert>
        </div>
      </>
    );
  }

  const licenseType = tenantData?.setting?.setting_license_type;
  const formattedCreditUsage = creditUsage
    ? {
        creditsLimit: formatDisplayCredits(creditUsage.creditsLimit),
        creditsUsed: formatDisplayCredits(creditUsage.creditsUsed),
        averageAnalysisCredits: formatDisplayCredits(
          creditUsage.averageAnalysisCredits,
        ),
        projectedCreditsUsed: formatDisplayCredits(
          creditUsage.projectedCreditsUsed,
        ),
        creditsRemaining: formatDisplayCredits(creditUsage.creditsRemaining),
        creditPeriodRemaining: formatCreditPeriodRemaining(
          creditUsage.periodEnd,
        ),
      }
    : null;

  const getPlanName = (licenseType: string) =>
    licenseType
      ? licenseType.charAt(0).toUpperCase() + licenseType.slice(1) + " Plan"
      : "Free Plan";

  const getPlanDescription = (licenseType: string) => {
    switch (licenseType) {
      case "business":
        return "Full access to advanced features, unlimited reports, priority support, and custom reports steps for your organization. Includes dedicated account management and enterprise-grade security.";
      case "pro":
        return "Access to advanced features and enhanced reporting capabilities.";
      case "free":
      default:
        return "Basic features and limited access to reports.";
    }
  };

  return (
    <>
      <title>Profile - Researchly</title>
      <div className="container mx-auto max-w-2xl py-8 space-y-6">
        <Card>
          <CardHeader>
            <CardTitle>Profile Settings</CardTitle>
            <CardDescription>
              Update your personal information and preferences.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="firstname">
                    First Name
                    {errors.firstname && (
                      <span className="ml-1 text-destructive">*</span>
                    )}
                  </Label>
                  <Input
                    id="firstname"
                    autoComplete="given-name"
                    aria-invalid={!!errors.firstname}
                    aria-describedby={
                      errors.firstname ? "firstname-error" : undefined
                    }
                    {...register("firstname", {
                      required: "First name is required",
                    })}
                    disabled={isPending}
                  />
                  {errors.firstname && (
                    <p
                      id="firstname-error"
                      className="text-xs text-destructive"
                      aria-live="polite"
                    >
                      {errors.firstname.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="lastname">
                    Last Name
                    {errors.lastname && (
                      <span className="ml-1 text-destructive">*</span>
                    )}
                  </Label>
                  <Input
                    id="lastname"
                    autoComplete="family-name"
                    aria-invalid={!!errors.lastname}
                    aria-describedby={
                      errors.lastname ? "lastname-error" : undefined
                    }
                    {...register("lastname", {
                      required: "Last name is required",
                    })}
                    disabled={isPending}
                  />
                  {errors.lastname && (
                    <p
                      id="lastname-error"
                      className="text-xs text-destructive"
                      aria-live="polite"
                    >
                      {errors.lastname.message}
                    </p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="position">Position</Label>
                  <Input
                    id="position"
                    autoComplete="organization-title"
                    placeholder="e.g., Software Engineer, Product Manager"
                    {...register("position")}
                    disabled={isPending}
                  />
                  <p className="text-xs text-muted-foreground">
                    Your job title or role.
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={user?.email || ""}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Email cannot be changed at this time.
                  </p>
                </div>
              </div>

              {updateError && (
                <Alert variant="destructive">
                  <CircleAlert />
                  <AlertTitle>Update failed</AlertTitle>
                  <AlertDescription>
                    {updateError instanceof Error
                      ? updateError.message
                      : "Failed to update profile"}
                  </AlertDescription>
                </Alert>
              )}

              <div className="flex justify-end">
                <Button type="submit" disabled={!isDirty || isPending}>
                  {isPending && (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  )}
                  Save Changes
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-start justify-between">
            <div className="space-y-1">
              <CardTitle>Subscription</CardTitle>
              <CardDescription>
                Organization-level subscription and billing.
              </CardDescription>
            </div>
            <ContactSalesButton />
          </CardHeader>
          <CardContent>
            <div className="rounded-lg bg-primary/10 p-4 space-y-4">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">
                  {getPlanName(licenseType || "free")}
                </h3>
                <span className="inline-flex items-center rounded-md bg-primary px-2.5 py-0.5 text-xs font-medium text-primary-foreground">
                  Active
                </span>
              </div>
              <p className="text-sm text-muted-foreground">
                {getPlanDescription(licenseType || "free")}
              </p>
            </div>
          </CardContent>
        </Card>
        <Card id="credits">
          <CardHeader className="flex flex-row items-start justify-between">
            <div className="space-y-1">
              <CardTitle>Credits</CardTitle>
              <CardDescription>
                Analysis credit usage for the current billing period.
              </CardDescription>
            </div>
            <ContactSalesButton />
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 rounded-lg bg-muted p-4 text-sm">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Used this period</span>
                <span className="font-medium">
                  {formattedCreditUsage
                    ? `${formattedCreditUsage.creditsUsed} / ${formattedCreditUsage.creditsLimit} credits`
                    : "No active subscription"}
                </span>
              </div>
              {creditUsage && formattedCreditUsage && (
                <>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      Remaining credits
                    </span>
                    <span className="font-medium">
                      {formattedCreditUsage.creditsRemaining} credits
                    </span>
                  </div>
                  {formattedCreditUsage.creditPeriodRemaining && (
                    <div className="flex items-center justify-between">
                      <span className="text-muted-foreground">
                        Credit period
                      </span>
                      <span className="font-medium">
                        {formattedCreditUsage.creditPeriodRemaining}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      Average analysis credit costs
                    </span>
                    <span className="font-medium">
                      {formattedCreditUsage.averageAnalysisCredits} credits
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      After next analysis
                    </span>
                    <span className="font-medium">
                      {formattedCreditUsage.projectedCreditsUsed} /{" "}
                      {formattedCreditUsage.creditsLimit} credits
                    </span>
                  </div>
                  {creditUsage.nextAnalysisStatus === "blocked_by_projection" &&
                    creditUsage.usageStatus !== "over_limit" && (
                      <div className="flex gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-amber-900">
                        <CircleAlert className="mt-0.5 h-4 w-4 shrink-0" />
                        <p>
                          You are currently within your credit limit, but
                          starting another analysis is blocked because the
                          average analysis credit costs would exceed the limit.
                        </p>
                      </div>
                    )}
                </>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Variables</CardTitle>
            <CardDescription>
              Configure global references and organization defaults for
              analyses.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {tenantVariableError && (
              <Alert variant="destructive" className="mb-4">
                <CircleAlert />
                <AlertTitle>Variable update failed</AlertTitle>
                <AlertDescription>
                  {tenantVariableError instanceof Error
                    ? tenantVariableError.message
                    : "Failed to update tenant variables"}
                </AlertDescription>
              </Alert>
            )}
            {isTenantVariablesLoading ? (
              <div className="flex items-center justify-center p-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : (
              <TenantVariableList
                variables={tenantVariables}
                isDeletingDefinition={isDeletingVariable}
                isSavingDefinition={isCreatingVariable || isUpdatingVariable}
                isSavingValue={isSavingVariableValue}
                onCreateVariable={createVariable}
                onUpdateVariable={(variableId, draft) =>
                  updateVariable({ variableId, draft })
                }
                onDeleteVariable={removeVariable}
                onSaveValue={saveVariableValue}
              />
            )}
          </CardContent>
        </Card>
      </div>
    </>
  );
}
